import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Breadcrumb } from "@/components/Breadcrumb";
import { getPracticeArea } from "@/data/practiceAreas";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

// Toggle to re-enable the "How We've Helped" / proof-points section on all
// practice pages. Data (area.howWeHelped) is preserved regardless of this flag.
const SHOW_PROOF_POINTS = false;

export default function PracticeAreaPage() {
  const { slug } = useParams();
  const area = getPracticeArea(slug);

  useDocumentMeta({
    title: area ? area.title : "Practice Areas",
    description: area ? area.description : undefined,
    path: `/practice/${slug}`,
  });

  if (!area) return <Navigate to="/" replace />;

  const Icon = area.icon;

  return (
    <div data-testid="practice-area-page">
      {/* Hero band */}
      <section className="relative bg-charcoal text-canvas overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-charcoal to-forest/40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-8 md:pt-24 md:pb-12">
          <Breadcrumb current={area.title} />

          <div className="mt-5 flex items-start gap-6">
            <span className="hidden md:inline-flex items-center justify-center w-10 h-10 border border-gold/40 text-gold shrink-0">
              <Icon className="w-4 h-4" strokeWidth={1.3} />
            </span>
            <div>
              <span className="font-sans text-[9px] tracking-[0.28em] uppercase text-gold">
                Practice / {area.index}
              </span>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                data-testid="practice-hero-title"
                className="mt-3 font-serif text-[17px] sm:text-[20px] lg:text-[32px] leading-[1.04] max-w-4xl"
              >
                {area.title}
              </motion.h1>
              <p className="mt-4 font-serif text-base md:text-lg text-canvas/70 max-w-2xl leading-snug">
                {area.heroLine}
              </p>
            </div>
          </div>

          <div className="mt-6 md:mt-8 flex justify-center" aria-hidden="true">
            <Icon className="w-[52px] h-[52px] text-gold/90" strokeWidth={0.9} />
          </div>
        </div>
      </section>

      {/* Overview (optional) */}
      {area.overview && area.overview.length > 0 && (
        <section data-testid="overview" className="bg-canvas pt-16 md:pt-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-12 bg-gold" />
                <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                  Overview
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight">
                The bigger picture.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-5">
              {area.overview.map((p, i) => (
                <p key={i} className="font-serif text-lg md:text-xl text-charcoal/80 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What We Do */}
      <section data-testid="what-we-do" className="bg-canvas py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-gold" />
              <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                What We Do
              </span>
            </div>
            <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight">
              Our scope of work.
            </h2>
          </div>

          <div className="lg:col-span-8">
            <ul className="border-t border-charcoal/12">
              {area.whatWeDo.map((point, i) => (
                <motion.li
                  key={i}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "0px 0px -80px 0px" }}
                  data-testid={`what-we-do-item-${i}`}
                  className="flex items-start gap-5 py-6 border-b border-charcoal/12"
                >
                  <span className="mt-1 inline-flex items-center justify-center w-6 h-6 border border-forest/30 text-forest shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={2} />
                  </span>
                  <p className="font-serif text-lg md:text-xl text-charcoal/80 leading-relaxed">
                    {point}
                  </p>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Promo Tool (optional) — external free reference tool */}
      {area.promoTool && (
        <section data-testid="promo-tool" className="bg-canvas pb-16 md:pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="border border-charcoal/15 bg-[#F1F1EE] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-5">
                <div className="flex items-center gap-4 mb-6">
                  <span className="h-px w-12 bg-gold" />
                  <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                    {area.promoTool.eyebrow}
                  </span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight">
                  {area.promoTool.title}
                </h2>
              </div>
              <div className="lg:col-span-7">
                <p className="font-serif text-lg md:text-xl text-charcoal/80 leading-relaxed">
                  {area.promoTool.body}
                </p>
                <a
                  href={area.promoTool.ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="promo-tool-cta"
                  className="mt-8 inline-flex items-center justify-center gap-2 font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-forest text-canvas border border-forest transition-colors duration-300 hover:bg-charcoal hover:border-charcoal"
                >
                  {area.promoTool.ctaText}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why AMADI Legal (optional) */}
      {area.whyUs && (
        <section data-testid="why-amadi-legal" className="bg-[#F1F1EE] py-16 md:py-20 border-y border-charcoal/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-12 bg-gold" />
                <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                  Why AMADI Legal
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight">
                Why work with us.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="font-serif text-lg md:text-xl text-charcoal/80 leading-relaxed">
                {area.whyUs}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* How We've Helped — hidden for now; flip SHOW_PROOF_POINTS to re-enable */}
      {SHOW_PROOF_POINTS && (
      <section data-testid="how-weve-helped" className="bg-[#F1F1EE] py-16 md:py-20 border-y border-charcoal/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-4 mb-4">
            <span className="h-px w-12 bg-gold" />
            <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
              How We&apos;ve Helped
            </span>
          </div>
          <h2 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight max-w-2xl mb-10">
            Selected proof points.
          </h2>

          <div className={`grid grid-cols-1 gap-px bg-charcoal/12 border border-charcoal/12 ${
            area.howWeHelped.length >= 3
              ? "md:grid-cols-3"
              : area.howWeHelped.length === 2
              ? "md:grid-cols-2"
              : "md:grid-cols-1"
          }`}>
            {area.howWeHelped.map((text, i) => (
              <motion.div
                key={i}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px 0px -80px 0px" }}
                data-testid={`proof-card-${i}`}
                className="group bg-canvas p-6 lg:p-8 flex flex-col justify-between min-h-[220px] hover:bg-forest transition-colors duration-400"
              >
                <span className="font-serif text-[34px] text-gold/70 group-hover:text-gold transition-colors duration-400">
                  0{i + 1}
                </span>
                <p className="mt-6 font-serif text-sm md:text-base text-charcoal/75 group-hover:text-canvas/80 leading-relaxed transition-colors duration-400">
                  {text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Related Practice Areas (optional) — cross-links only */}
      {area.related && area.related.length > 0 && (
        <section data-testid="related-practices" className="bg-canvas py-16 md:py-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-gold" />
              <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-forest">
                Related Practice Areas
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {area.related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/practice/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group flex items-center justify-between gap-4 border border-charcoal/15 p-6 hover:border-forest transition-colors duration-300"
                >
                  <span className="font-serif text-lg md:text-xl text-charcoal group-hover:text-forest transition-colors duration-300">
                    {r.title}
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-forest shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
